import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import type { SalesOrder, SalesOrderStatus, SalesOrderItem } from '@/types'
import type { SalesOrdersState } from './state'

export interface CreateSalesOrderData {
  quotationId?: string
  projectId?: string
  organizationId: string
  items: Omit<SalesOrderItem, 'total'>[]
  taxRate?: number
  discount?: number
  notes?: string
  validUntil?: Date
}

export interface UpdateSalesOrderData {
  items?: Omit<SalesOrderItem, 'total'>[]
  taxRate?: number
  discount?: number
  notes?: string
  status?: SalesOrderStatus
  validUntil?: Date
}

const generateOrderNumber = (): string => {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
  return `OV-${year}${month}-${random}`
}

const calculateTotals = (items: SalesOrderItem[], taxRate = 0, discount = 0) => {
  const subtotal = items.reduce((sum, item) => sum + item.total, 0)
  const taxAmount = subtotal * (taxRate / 100)
  const total = subtotal + taxAmount - discount
  return { subtotal, taxAmount, total }
}

export const createActions = (state: SalesOrdersState) => {
  const { logCreate, logUpdate, logDelete, detectChanges } = useActivityLog()
  const notificationsStore = useNotificationsStore()

  let globalUnsubscribe: Unsubscribe | null = null

  const mapSalesOrderDoc = (docSnap: { id: string; data: () => Record<string, unknown> }): SalesOrder => {
    const data = docSnap.data()
    return {
      id: docSnap.id,
      tenantId: data.tenantId,
      orderNumber: data.orderNumber,
      quotationId: data.quotationId,
      projectId: data.projectId,
      organizationId: data.organizationId,
      items: data.items || [],
      subtotal: data.subtotal,
      taxRate: data.taxRate || 0,
      taxAmount: data.taxAmount || 0,
      discount: data.discount || 0,
      total: data.total,
      status: data.status,
      notes: data.notes,
      validUntil: (data.validUntil as any)?.toDate?.(),
      acceptedAt: (data.acceptedAt as any)?.toDate?.(),
      createdBy: data.createdBy || '',
      createdAt: (data.createdAt as any)?.toDate?.() || new Date(),
      updatedAt: (data.updatedAt as any)?.toDate?.() || new Date(),
      updatedBy: data.updatedBy || '',
    } as SalesOrder
  }

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return
    state.isLoading = true
    const authStore = useAuthStore()
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []

    let q = query(collection(db, 'salesOrders'), ...baseConstraints, orderBy('createdAt', 'desc'))
    if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
      if (authStore.user.organizationId) {
        q = query(collection(db, 'salesOrders'), ...baseConstraints, where('organizationId', '==', authStore.user.organizationId), orderBy('createdAt', 'desc'))
      }
    }

    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      state.salesOrders = snapshot.docs.map(mapSalesOrderDoc)
      state.isLoading = false
    }, (error) => {
      console.error('SalesOrders subscription error:', error)
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchSalesOrders = async (): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []

      let q = query(collection(db, 'salesOrders'), ...baseConstraints, orderBy('createdAt', 'desc'))

      // Filter by organization for clients
      if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
        if (authStore.user.organizationId) {
          q = query(
            collection(db, 'salesOrders'),
            ...baseConstraints,
            where('organizationId', '==', authStore.user.organizationId),
            orderBy('createdAt', 'desc')
          )
        }
      }

      const snapshot = await getDocs(q)

      state.salesOrders = snapshot.docs.map((docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          tenantId: data.tenantId,
          orderNumber: data.orderNumber,
          quotationId: data.quotationId,
          projectId: data.projectId,
          organizationId: data.organizationId,
          items: data.items || [],
          subtotal: data.subtotal,
          taxRate: data.taxRate || 0,
          taxAmount: data.taxAmount || 0,
          discount: data.discount || 0,
          total: data.total,
          status: data.status,
          notes: data.notes,
          validUntil: data.validUntil?.toDate(),
          acceptedAt: data.acceptedAt?.toDate(),
          createdBy: data.createdBy || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          updatedBy: data.updatedBy || '',
        } as SalesOrder
      })
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching sales orders'
      console.error('Error fetching sales orders:', error)
    } finally {
      state.isLoading = false
    }
  }

  const fetchSalesOrderById = async (id: string): Promise<SalesOrder | null> => {
    try {
      const docRef = doc(db, 'salesOrders', id)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      const data = docSnap.data()
      return {
        id: docSnap.id,
        tenantId: data.tenantId,
        orderNumber: data.orderNumber,
        quotationId: data.quotationId,
        projectId: data.projectId,
        organizationId: data.organizationId,
        items: data.items || [],
        subtotal: data.subtotal,
        taxRate: data.taxRate || 0,
        taxAmount: data.taxAmount || 0,
        discount: data.discount || 0,
        total: data.total,
        status: data.status,
        notes: data.notes,
        validUntil: data.validUntil?.toDate(),
        acceptedAt: data.acceptedAt?.toDate(),
        createdBy: data.createdBy || '',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        updatedBy: data.updatedBy || '',
      } as SalesOrder
    } catch (error) {
      console.error('Error fetching sales order:', error)
      return null
    }
  }

  const createSalesOrder = async (data: CreateSalesOrderData): Promise<string> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''

    // Calculate item totals
    const itemsWithTotals: SalesOrderItem[] = data.items.map((item) => ({
      ...item,
      total: item.quantity * item.unitPrice,
    }))

    const { subtotal, taxAmount, total } = calculateTotals(
      itemsWithTotals,
      data.taxRate,
      data.discount
    )

    const orderNumber = generateOrderNumber()

    const orderData = {
      ...getTenantData(),
      orderNumber,
      quotationId: data.quotationId || null,
      projectId: data.projectId || null,
      organizationId: data.organizationId,
      items: itemsWithTotals,
      subtotal,
      taxRate: data.taxRate || 0,
      taxAmount,
      discount: data.discount || 0,
      total,
      status: 'draft' as SalesOrderStatus,
      notes: data.notes || null,
      validUntil: data.validUntil || null,
      createdBy: userId,
      updatedBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    const docRef = await addDoc(collection(db, 'salesOrders'), orderData)

    const newOrder: SalesOrder = {
      id: docRef.id,
      orderNumber,
      quotationId: data.quotationId,
      projectId: data.projectId,
      organizationId: data.organizationId,
      items: itemsWithTotals,
      subtotal,
      taxRate: data.taxRate || 0,
      taxAmount,
      discount: data.discount,
      total,
      status: 'draft',
      notes: data.notes,
      validUntil: data.validUntil,
      createdBy: userId,
      updatedBy: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    state.salesOrders.unshift(newOrder)

    await logCreate('salesOrder', docRef.id, orderNumber)

    // Notify admins and operators about new sales order
    await notificationsStore.notifyAdminsAndOperators(
      'sales_order_created',
      'Nueva orden de venta creada',
      `Se ha creado la orden de venta ${orderNumber}`,
      'salesOrder',
      docRef.id
    )

    return docRef.id
  }

  const updateSalesOrder = async (id: string, data: UpdateSalesOrderData): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'salesOrders', id)
    const existingOrder = state.salesOrders.find((o) => o.id === id)

    let updateData: Record<string, unknown> = {
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    }

    // If items are being updated, recalculate totals
    if (data.items) {
      const itemsWithTotals: SalesOrderItem[] = data.items.map((item) => ({
        ...item,
        total: item.quantity * item.unitPrice,
      }))

      const taxRate = data.taxRate ?? existingOrder?.taxRate ?? 0
      const discount = data.discount ?? existingOrder?.discount ?? 0
      const { subtotal, taxAmount, total } = calculateTotals(itemsWithTotals, taxRate, discount)

      updateData = {
        ...updateData,
        items: itemsWithTotals,
        subtotal,
        taxAmount,
        total,
      }
    }

    if (data.taxRate !== undefined) updateData.taxRate = data.taxRate
    if (data.discount !== undefined) updateData.discount = data.discount
    if (data.notes !== undefined) updateData.notes = data.notes
    if (data.status !== undefined) updateData.status = data.status
    if (data.validUntil !== undefined) updateData.validUntil = data.validUntil

    await updateDoc(docRef, updateData)

    const index = state.salesOrders.findIndex((o) => o.id === id)
    if (index !== -1) {
      // Build the updated items if provided
      const updatedItems = data.items
        ? data.items.map((item) => ({ ...item, total: item.quantity * item.unitPrice }))
        : state.salesOrders[index].items

      state.salesOrders[index] = {
        ...state.salesOrders[index],
        ...data,
        items: updatedItems,
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }

    if (existingOrder) {
      const fieldsToTrack: (keyof SalesOrder)[] = ['status', 'total']
      const changes = detectChanges(
        existingOrder as unknown as Record<string, unknown>,
        data as Record<string, unknown>,
        fieldsToTrack as string[]
      )
      if (changes.length > 0) {
        await logUpdate('salesOrder', id, changes, existingOrder.orderNumber)
      }
    }
  }

  const updateOrderStatus = async (id: string, status: SalesOrderStatus): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'salesOrders', id)
    const existingOrder = state.salesOrders.find((o) => o.id === id)

    const updatePayload: Record<string, unknown> = {
      status,
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    }

    // If accepted, set acceptedAt
    if (status === 'accepted') {
      updatePayload.acceptedAt = serverTimestamp()
    }

    await updateDoc(docRef, updatePayload)

    const index = state.salesOrders.findIndex((o) => o.id === id)
    if (index !== -1) {
      state.salesOrders[index] = {
        ...state.salesOrders[index],
        status,
        acceptedAt: status === 'accepted' ? new Date() : state.salesOrders[index].acceptedAt,
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }

    if (existingOrder) {
      await logUpdate('salesOrder', id, [
        { field: 'status', oldValue: existingOrder.status, newValue: status },
      ], existingOrder.orderNumber)
    }
  }

  const deleteSalesOrder = async (id: string): Promise<void> => {
    const order = state.salesOrders.find((o) => o.id === id)

    await deleteDoc(doc(db, 'salesOrders', id))

    state.salesOrders = state.salesOrders.filter((o) => o.id !== id)

    if (order) {
      await logDelete('salesOrder', id, order.orderNumber)
    }
  }

  const setFilters = (filters: Partial<SalesOrdersState['filters']>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const getSalesOrderById = (id: string): SalesOrder | undefined => {
    return state.salesOrders.find((o) => o.id === id)
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchSalesOrders,
    fetchSalesOrderById,
    createSalesOrder,
    updateSalesOrder,
    updateOrderStatus,
    deleteSalesOrder,
    setFilters,
    getSalesOrderById,
  }
}
